<script setup lang="ts" generic="Value extends string | number | null">
import { computed, nextTick, ref, watch } from 'vue'

const props = defineProps<{
  modelValue: Value
  name?: string
  placeholder?: string
  /**Function that formats the text when not being edited */
  formatter?: (value: Value) => string
  /**Function that runs on value update to process the changed data.
   * By default this is set to a function that will convert empty strings to undefined
   */
  converter?: (value: Value) => unknown
}>()
const emit = defineEmits<{
  update: [value: unknown]
}>()
defineOptions({ inheritAttrs: false })

watch(
  () => props.modelValue,
  () => {
    localValue.value = props.modelValue
  },
)

const localValue = ref<Value>(props.modelValue)
const isFocused = ref(false)

const inputRef = ref<HTMLInputElement | null>(null)

function focusInput() {
  isFocused.value = true
  nextTick(() => inputRef.value?.focus())
}

const formattedValue = computed(() => {
  return props.formatter && localValue.value ? props.formatter(localValue.value) : localValue.value
})

function updateValue() {
  if (!localValue.value) {
    localValue.value = undefined
  }
  if (props.converter) {
    localValue.value = props.converter(localValue.value)
  }
  emit('update', localValue.value)
}
</script>

<template>
  <div class="flex w-full flex-col">
    <label v-if="props.name" :for="props.name" class="mb-2 block text-sm font-medium">{{
      props.name
    }}</label>
    <div
      @click="focusInput"
      class="block w-full rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
    >
      <!-- TODO - Make border color work correctly when focused. Currently shows black border when focused -->
      <input
        class="peer h-full w-full p-2.5"
        v-if="isFocused"
        ref="inputRef"
        v-model="localValue"
        @change="updateValue()"
        v-bind="$attrs"
        :name="props.name"
        :id="props.name"
        @blur="isFocused = false"
        onfocus="this.select()"
        :placeholder="props.placeholder"
      />
      <input
        v-else
        :id="props.name"
        @focus="focusInput"
        :value="formattedValue"
        :placeholder="props.placeholder"
        readonly
        class="h-full w-full overflow-hidden p-2.5 text-ellipsis whitespace-nowrap"
      />
    </div>
  </div>
</template>
